// <s-type-on> types its heading text in when it scrolls into view, one letter at a
// time for Latin text and one word at a time for Arabic (so joined letters keep their
// shape). Reading order follows the text itself, so Arabic types right to left.
// Screen readers get the full text from a visually hidden copy; reduced-motion users
// see the heading unchanged.

const ARABIC = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

class TypeOn extends HTMLElement {
  #observer;
  #units = [];

  connectedCallback() {
    if (this.hasAttribute('data-ready')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const text = this.textContent.trim();
    if (!text) return;

    this.#split(text);
    this.setAttribute('data-ready', '');

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer.disconnect();
        this.#play();
      },
      { threshold: 0.3 },
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #split(text) {
    const visual = document.createElement('span');
    visual.setAttribute('aria-hidden', 'true');

    const visualRoot = this.cloneNode(true);
    const cloneWalker = document.createTreeWalker(visualRoot, NodeFilter.SHOW_TEXT);
    const cloneNodes = [];
    while (cloneWalker.nextNode()) cloneNodes.push(cloneWalker.currentNode);

    for (const node of cloneNodes) {
      const value = node.nodeValue;
      if (!value.trim()) continue;
      const pieces = ARABIC.test(value) ? value.split(/(\s+)/) : Array.from(value);
      const fragment = document.createDocumentFragment();
      for (const piece of pieces) {
        if (!piece) continue;
        if (!piece.trim()) {
          fragment.append(piece);
          continue;
        }
        const unit = document.createElement('span');
        unit.textContent = piece;
        unit.style.opacity = '0';
        this.#units.push(unit);
        fragment.append(unit);
      }
      node.replaceWith(fragment);
    }

    visual.append(...visualRoot.childNodes);

    const readable = document.createElement('span');
    readable.className = 'sr-only';
    readable.textContent = text;

    this.replaceChildren(readable, visual);
  }

  #play() {
    const count = this.#units.length;
    if (!count) return;
    // Long headings type faster so every heading finishes within about 1.6s.
    const step = Math.min(55, 1600 / count);
    this.#units.forEach((unit, index) => {
      const animation = unit.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 160,
        delay: index * step,
        easing: EASE,
        fill: 'forwards',
      });
      animation.finished.then(() => {
        unit.style.opacity = '1';
        animation.cancel();
      });
    });
  }
}

if (!customElements.get('s-type-on')) {
  customElements.define('s-type-on', TypeOn);
}
